import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Video, Upload, Play, BarChart3, Users, Target, Zap, Star, TrendingUp,
  Download, Eye, Loader2, Brain, Award, Activity, Timer, ChevronRight,
  UploadCloud, X, Shield, User as UserIcon, Hash,
} from 'lucide-react';
import { toast } from 'sonner';
import PlayerProgressionChart from '@/components/admin/PlayerProgressionChart';
import ScoutingComparisonChart from '@/components/admin/ScoutingComparisonChart';
import Phase167DigestPanel from '@/components/admin/Phase167DigestPanel';
import PlayerHeroCard from '@/components/admin/PlayerHeroCard';

export default function VideoAnalysis() {
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const [form, setForm] = useState({
    title: '', video_url: '', sport: 'football', notes: '',
    target_team: 'Home', focus_type: 'Team', jersey_number: '',
    source_filename: '', source_filesize: 0,
  });
  const [dragActive, setDragActive] = useState(false);

  const fetchData = useCallback(async () => {
    try { const r = await api.get('/video-analysis'); setAnalyses(r.data); }
    catch (err) { console.error(err); } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Poll for processing analyses
  useEffect(() => {
    const processing = analyses.filter(a => a.status === 'processing');
    if (processing.length > 0) {
      const poll = setInterval(fetchData, 5000);
      return () => clearInterval(poll);
    }
  }, [analyses, fetchData]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.title) { toast.error('Title required'); return; }
    if (!form.video_url && !form.source_filename) {
      toast.error('Either drop a video file or paste a URL');
      return;
    }
    setCreating(true);
    try {
      const r = await api.post('/video-analysis/create', form);
      toast.success('Analysis started! AI is processing...');
      setShowCreate(false);
      setForm({
        title: '', video_url: '', sport: 'football', notes: '',
        target_team: 'Home', focus_type: 'Team', jersey_number: '',
        source_filename: '', source_filesize: 0,
      });
      fetchData();
    } catch (err) { toast.error('Failed to create'); }
    finally { setCreating(false); }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) attachFile(f);
  };

  const handleBrowse = (e) => {
    const f = e.target.files?.[0];
    if (f) attachFile(f);
  };

  const attachFile = (f) => {
    const sizeMB = (f.size / (1024 * 1024)).toFixed(1);
    if (f.size > 4 * 1024 * 1024 * 1024) {
      toast.error(`File too large (${sizeMB} MB). Max 4 GB.`);
      return;
    }
    setForm((p) => ({ ...p, source_filename: f.name, source_filesize: f.size }));
  };

  const viewAnalysis = async (id) => {
    try { const r = await api.get(`/video-analysis/${id}`); setSelectedAnalysis(r.data); }
    catch { toast.error('Failed to load'); }
  };

  const getKpiColor = (val, thresholds) => {
    if (val >= thresholds[0]) return 'text-emerald-600';
    if (val >= thresholds[1]) return 'text-amber-600';
    return 'text-red-600';
  };

  const getKpiBg = (val, thresholds) => {
    if (val >= thresholds[0]) return 'bg-emerald-50 border-emerald-200';
    if (val >= thresholds[1]) return 'bg-amber-50 border-amber-200';
    return 'bg-red-50 border-red-200';
  };

  if (loading) return <div className="space-y-4"><Skeleton className="h-8 w-48" /><div className="grid grid-cols-3 gap-4">{[1,2,3].map(i => <Skeleton key={i} className="h-48 rounded-xl" />)}</div></div>;

  return (
    <div className="space-y-6" data-testid="video-analysis-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-display font-semibold">Video Analysis</h1>
          <p className="text-sm text-muted-foreground">AI-powered match & training video intelligence</p>
        </div>
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogTrigger asChild>
            <Button className="bg-purple-600 hover:bg-purple-700"><Video className="h-4 w-4 mr-2" /> New Analysis</Button>
          </DialogTrigger>
          <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="video-analysis-create-modal">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-slate-900">
                <Brain className="h-5 w-5 text-purple-600" /> Create Video Intelligence Analysis
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 text-slate-900">
              {/* Title */}
              <div className="space-y-1.5">
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Analysis Title *</Label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="e.g., U13 National Championship Semi-Final"
                  required
                  data-testid="video-analysis-title-input"
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-slate-900 font-medium placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-600 focus:bg-white transition-all"
                />
              </div>

              {/* Drag & Drop Upload Zone */}
              <div className="space-y-1.5">
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Match or Training Video File</Label>
                <div
                  onDragEnter={handleDrag}
                  onDragOver={handleDrag}
                  onDragLeave={handleDrag}
                  onDrop={handleDrop}
                  data-testid="video-analysis-dropzone"
                  className={`relative border-2 border-dashed rounded-xl p-6 text-center flex flex-col items-center justify-center transition-all ${
                    dragActive ? 'border-purple-600 bg-purple-50/60' : 'border-slate-200 bg-slate-50/50 hover:bg-slate-50'
                  }`}
                >
                  {form.source_filename ? (
                    <div className="space-y-2" data-testid="video-analysis-file-attached">
                      <p className="text-sm font-semibold text-slate-800 break-all">✅ {form.source_filename}</p>
                      <p className="text-[11px] text-slate-500">{(form.source_filesize / (1024 * 1024)).toFixed(1)} MB</p>
                      <button
                        type="button"
                        onClick={() => setForm((p) => ({ ...p, source_filename: '', source_filesize: 0 }))}
                        className="text-xs font-bold text-rose-600 hover:underline"
                        data-testid="video-analysis-file-remove"
                      >
                        Remove File
                      </button>
                    </div>
                  ) : (
                    <>
                      <UploadCloud className="w-8 h-8 text-slate-400 mb-2" />
                      <p className="text-sm text-slate-700 font-medium">
                        Drag &amp; drop your match footage here, or{' '}
                        <label className="text-purple-600 font-semibold cursor-pointer hover:underline" data-testid="video-analysis-browse-label">
                          browse files
                          <input
                            type="file"
                            accept="video/mp4,video/quicktime,video/x-msvideo,video/*"
                            className="hidden"
                            onChange={handleBrowse}
                            data-testid="video-analysis-file-input"
                          />
                        </label>
                      </p>
                      <p className="text-[11px] text-slate-400 mt-1">Supports MP4, MOV, AVI up to 4&nbsp;GB</p>
                    </>
                  )}
                </div>
              </div>

              {/* Or paste a URL — backend processing path */}
              <div className="space-y-1.5">
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Or Paste a Public Video URL</Label>
                <input
                  type="text"
                  value={form.video_url}
                  onChange={(e) => setForm({ ...form, video_url: e.target.value })}
                  placeholder="https://youtube.com/watch?v=… or direct MP4 URL"
                  data-testid="video-analysis-url-input"
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-slate-900 font-medium placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-600 focus:bg-white transition-all"
                />
                <p className="text-[10px] text-slate-400">Direct-file processing is in beta. URL ingestion is the active CV path.</p>
              </div>

              {/* Scouting context — target team + focus + jersey */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 inline-flex items-center gap-1">
                    <Shield className="h-3 w-3" /> Target Team
                  </Label>
                  <select
                    value={form.target_team}
                    onChange={(e) => setForm({ ...form, target_team: e.target.value })}
                    data-testid="video-analysis-target-team-select"
                    className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-slate-900 font-medium focus:outline-none focus:ring-2 focus:ring-purple-600 focus:bg-white"
                  >
                    <option value="Home">Home</option>
                    <option value="Away">Away</option>
                    <option value="Both">Both Teams</option>
                  </select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 inline-flex items-center gap-1">
                    <UserIcon className="h-3 w-3" /> Analysis Boundary
                  </Label>
                  <select
                    value={form.focus_type}
                    onChange={(e) => setForm({ ...form, focus_type: e.target.value })}
                    data-testid="video-analysis-focus-type-select"
                    className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-slate-900 font-medium focus:outline-none focus:ring-2 focus:ring-purple-600 focus:bg-white"
                  >
                    <option value="Team">Full Team Analysis</option>
                    <option value="Player">Individual Player Tracking</option>
                  </select>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 inline-flex items-center gap-1">
                  <Hash className="h-3 w-3" /> Target Jersey / Player ID
                </Label>
                <input
                  type="text"
                  value={form.jersey_number}
                  onChange={(e) => setForm({ ...form, jersey_number: e.target.value })}
                  disabled={form.focus_type === 'Team'}
                  placeholder={form.focus_type === 'Team' ? 'N/A — Full Squad' : 'e.g., #10 (Striker)'}
                  data-testid="video-analysis-jersey-input"
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-slate-900 font-medium placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-600 focus:bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                />
              </div>

              {/* Sport */}
              <div className="space-y-1.5">
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Sport Discipline</Label>
                <select
                  value={form.sport}
                  onChange={(e) => setForm({ ...form, sport: e.target.value })}
                  data-testid="video-analysis-sport-select"
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-slate-900 font-medium focus:outline-none focus:ring-2 focus:ring-purple-600 focus:bg-white"
                >
                  <option value="football">Football / Soccer</option>
                  <option value="cricket">Cricket</option>
                  <option value="basketball">Basketball</option>
                  <option value="badminton">Badminton</option>
                  <option value="swimming">Swimming</option>
                  <option value="other">Other</option>
                </select>
              </div>

              {/* Notes */}
              <div className="space-y-1.5">
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Notes</Label>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Match details, key players to watch, tactical focus areas…"
                  rows={2}
                  data-testid="video-analysis-notes-input"
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-slate-900 font-medium placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-600 focus:bg-white transition-all resize-none"
                />
              </div>

              <Button
                type="submit"
                disabled={creating}
                data-testid="video-analysis-submit-btn"
                className="w-full bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 px-4 rounded-xl shadow-lg shadow-purple-200 transition-all flex items-center justify-center gap-2 mt-1"
              >
                {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
                {creating ? 'Starting AI Analysis…' : 'Initialize Computer Vision Tracking Engine'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Analysis Grid */}
      {analyses.length === 0 && !selectedAnalysis ? (
        <Card className="border-dashed"><CardContent className="flex flex-col items-center justify-center py-16">
          <Video className="h-12 w-12 text-muted-foreground/40 mb-4" />
          <h3 className="text-lg font-semibold mb-1">No analyses yet</h3>
          <p className="text-sm text-muted-foreground mb-4">Upload a match video to get AI-powered tactical insights</p>
          <Button onClick={() => setShowCreate(true)} className="bg-purple-600 hover:bg-purple-700"><Video className="h-4 w-4 mr-2" /> Create First Analysis</Button>
        </CardContent></Card>
      ) : !selectedAnalysis ? (
        <>
          {/* Phase 167 — Weekly Scout Heatmap Email Digest control panel */}
          <Phase167DigestPanel />

          {/* Phase 162 — Player Progression Radar (Legend Tier scouting tool) */}
          <PlayerProgressionChart />

          {/* Phase 163 — Comparative Side-by-Side Scouting + AI Verdict */}
          <ScoutingComparisonChart />

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {analyses.map(a => (
            <Card key={a.id} className="hover:shadow-lg transition-shadow cursor-pointer group" onClick={() => viewAnalysis(a.id)}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <Badge className={`text-xs border-0 ${a.status === 'complete' ? 'bg-emerald-100 text-emerald-700' : a.status === 'processing' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>
                    {a.status === 'processing' && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
                    {a.status}
                  </Badge>
                  <Badge variant="outline" className="text-xs capitalize">{a.sport}</Badge>
                </div>
                <h3 className="font-semibold text-sm mb-1 group-hover:text-purple-600 transition-colors">{a.title}</h3>
                <p className="text-xs text-muted-foreground mb-3">{a.created_at?.slice(0, 10)}</p>
                {a.kpis && (
                  <div className="grid grid-cols-3 gap-2">
                    <div className="text-center p-1.5 rounded-lg bg-muted/50"><p className="text-xs text-muted-foreground">Poss</p><p className="text-sm font-bold tabular-nums">{a.kpis.possession}%</p></div>
                    <div className="text-center p-1.5 rounded-lg bg-muted/50"><p className="text-xs text-muted-foreground">xG</p><p className="text-sm font-bold tabular-nums">{a.kpis.xg}</p></div>
                    <div className="text-center p-1.5 rounded-lg bg-muted/50"><p className="text-xs text-muted-foreground">PPDA</p><p className="text-sm font-bold tabular-nums">{a.kpis.ppda}</p></div>
                  </div>
                )}
                {a.status === 'processing' && <Progress value={60} className="mt-3 h-1.5" />}
              </CardContent>
            </Card>
          ))}
          </div>
        </>
      ) : (
        /* Detailed Analysis View */
        <div className="space-y-4">
          <Button variant="ghost" size="sm" onClick={() => setSelectedAnalysis(null)}>← Back to all analyses</Button>

          {/* Header */}
          <Card className="overflow-hidden">
            <div className="bg-gradient-to-r from-purple-600 to-violet-600 px-6 py-5 text-white">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="min-w-0">
                  <h2 className="text-xl font-display font-bold" data-testid="video-analysis-detail-title">{selectedAnalysis.title}</h2>
                  <p className="text-purple-200 text-sm mt-0.5">{selectedAnalysis.created_at?.slice(0, 10)} • {selectedAnalysis.sport} • AI Analysis</p>
                  {/* Phase 160 — Scouting context tags */}
                  <div className="flex flex-wrap gap-1.5 mt-2.5" data-testid="video-analysis-context-tags">
                    {selectedAnalysis.target_team && (
                      <Badge className="bg-white/15 hover:bg-white/20 text-white border-white/25 text-[11px] font-semibold" data-testid="video-analysis-tag-team">
                        <Shield className="h-3 w-3 mr-1" /> Team: {selectedAnalysis.target_team}
                      </Badge>
                    )}
                    {selectedAnalysis.focus_type && (
                      <Badge className="bg-white/15 hover:bg-white/20 text-white border-white/25 text-[11px] font-semibold" data-testid="video-analysis-tag-focus">
                        <UserIcon className="h-3 w-3 mr-1" />
                        {selectedAnalysis.focus_type === 'Player' ? 'Individual Tracking' : 'Full Team'}
                      </Badge>
                    )}
                    {selectedAnalysis.jersey_number && selectedAnalysis.focus_type === 'Player' && (
                      <Badge className="bg-amber-400/90 text-amber-950 border-amber-300 text-[11px] font-bold" data-testid="video-analysis-tag-jersey">
                        <Hash className="h-3 w-3 mr-1" /> Jersey {selectedAnalysis.jersey_number}
                      </Badge>
                    )}
                    {selectedAnalysis.source_filename && (
                      <Badge className="bg-white/15 hover:bg-white/20 text-white border-white/25 text-[11px] font-semibold" data-testid="video-analysis-tag-source">
                        <Video className="h-3 w-3 mr-1" /> {selectedAnalysis.source_filename}
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <Badge className="bg-white/20 text-white border-white/30">{selectedAnalysis.status}</Badge>
                  <Button variant="outline" size="sm" className="border-white/20 text-white hover:bg-white/10" onClick={() => {
                    const kpis = selectedAnalysis.kpis || {};
                    const players = selectedAnalysis.player_analysis || [];
                    const ctx = [];
                    if (selectedAnalysis.target_team) ctx.push(`Team: ${selectedAnalysis.target_team}`);
                    if (selectedAnalysis.focus_type) ctx.push(`Focus: ${selectedAnalysis.focus_type === 'Player' ? 'Individual Tracking' : 'Full Team'}`);
                    if (selectedAnalysis.jersey_number) ctx.push(`Jersey ${selectedAnalysis.jersey_number}`);
                    const ctxLine = ctx.length ? `<p style="opacity:0.85;margin:4px 0 0;font-size:13px">${ctx.join(' • ')}</p>` : '';
                    const html = `<html><head><title>Analysis Report</title></head><body style="font-family:sans-serif;max-width:700px;margin:40px auto;padding:20px"><div style="background:linear-gradient(to right,#7c3aed,#8b5cf6);color:white;padding:20px;border-radius:12px"><h2 style="margin:0">${selectedAnalysis.title}</h2><p style="opacity:0.8;margin:4px 0 0">${selectedAnalysis.sport} | ${selectedAnalysis.created_at?.slice(0,10)}</p>${ctxLine}</div><h3>Key Metrics</h3><table style="width:100%;border-collapse:collapse"><tr><td style="padding:8px;border:1px solid #e2e8f0"><strong>Possession</strong><br>${kpis.possession}%</td><td style="padding:8px;border:1px solid #e2e8f0"><strong>xG</strong><br>${kpis.xg}</td><td style="padding:8px;border:1px solid #e2e8f0"><strong>PPDA</strong><br>${kpis.ppda}</td><td style="padding:8px;border:1px solid #e2e8f0"><strong>Pass Acc</strong><br>${kpis.pass_accuracy}%</td></tr></table><h3>Player Ratings</h3>${players.map(p => '<div style="padding:10px;border:1px solid #e2e8f0;border-radius:8px;margin:8px 0"><strong>'+p.name+'</strong> - '+p.rating+'/10<br><small>'+p.ai_recommendation+'</small></div>').join('')}<h3>AI Insights</h3>${(selectedAnalysis.ai_insights||[]).map(i => '<p>• '+i.text+'</p>').join('')}<p style="text-align:center;color:#94a3b8;font-size:11px;margin-top:30px">Powered by Out-Play.in + TwelveLabs AI</p></body></html>`;
                    const w = window.open('', '_blank'); w.document.write(html); w.document.close();
                  }}><Download className="h-3.5 w-3.5 mr-1" /> Report</Button>
                </div>
              </div>
            </div>
          </Card>

          {/* KPI Dashboard */}
          {selectedAnalysis.kpis && (() => {
            const k = selectedAnalysis.kpis;
            return (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                {[
                  { label: 'Possession', value: `${k.possession}%`, icon: Activity, thresholds: [55, 45] },
                  { label: 'PPDA', value: k.ppda, icon: Target, thresholds: [10, 8] },
                  { label: 'xG', value: k.xg, icon: Zap, thresholds: [1.5, 0.8] },
                  { label: 'xGA', value: k.xga, icon: TrendingUp, thresholds: [1.0, 0.5] },
                  { label: 'Pass Acc', value: `${k.pass_accuracy}%`, icon: BarChart3, thresholds: [82, 72] },
                  { label: 'Shots', value: `${k.shots_on_target}/${k.shots}`, icon: Target, thresholds: [6, 3] },
                  { label: 'Tackles Won', value: `${k.tackles_won}%`, icon: Users, thresholds: [65, 55] },
                  { label: 'Prog. Passes', value: k.progressive_passes, icon: ChevronRight, thresholds: [25, 15] },
                  { label: 'Half-Space', value: `${k.half_space_touches}%`, icon: Star, thresholds: [75, 60] },
                  { label: 'Aerial Won', value: `${k.aerial_duels_won}%`, icon: TrendingUp, thresholds: [55, 45] },
                  { label: 'Corners', value: k.corners, icon: Activity, thresholds: [5, 3] },
                  { label: 'Shots OT%', value: `${k.shots_on_target && k.shots ? Math.round(k.shots_on_target/k.shots*100) : 0}%`, icon: Target, thresholds: [40, 25] },
                ].map(kpi => {
                  const Icon = kpi.icon;
                  const numVal = parseFloat(String(kpi.value).replace('%', ''));
                  return (
                    <Card key={kpi.label} className={`border ${getKpiBg(numVal, kpi.thresholds)}`}>
                      <CardContent className="p-3 text-center">
                        <Icon className={`h-4 w-4 mx-auto mb-1 ${getKpiColor(numVal, kpi.thresholds)}`} />
                        <p className={`text-lg font-display font-bold tabular-nums ${getKpiColor(numVal, kpi.thresholds)}`}>{kpi.value}</p>
                        <p className="text-[10px] text-muted-foreground">{kpi.label}</p>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            );
          })()}

          {/* Phase 175-C — Branded Player Hero Card (Player focus only) */}
          {selectedAnalysis.focus_type === 'Player' && (
            <PlayerHeroCard
              jerseyNumber={selectedAnalysis.jersey_number}
              academyName={selectedAnalysis.academy_name || selectedAnalysis.title}
              sport={selectedAnalysis.sport}
              matchDate={selectedAnalysis.match_date}
              opponent={selectedAnalysis.opponent}
              status="Scouting Pool"
            />
          )}

          {/* AI Insights */}
          {selectedAnalysis.ai_insights && (
            <Card><CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Brain className="h-4 w-4 text-purple-600" /> AI Tactical Insights</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {selectedAnalysis.ai_insights.map((ins, i) => (
                  <div key={i} className={`flex items-start gap-2 p-2.5 rounded-lg ${ins.type === 'tactical' ? 'bg-blue-50 dark:bg-blue-950/20' : ins.type === 'highlight' ? 'bg-emerald-50 dark:bg-emerald-950/20' : ins.type === 'improvement' ? 'bg-amber-50 dark:bg-amber-950/20' : 'bg-purple-50 dark:bg-purple-950/20'}`}>
                    <Zap className={`h-4 w-4 mt-0.5 flex-shrink-0 ${ins.type === 'tactical' ? 'text-blue-600' : ins.type === 'highlight' ? 'text-emerald-600' : ins.type === 'improvement' ? 'text-amber-600' : 'text-purple-600'}`} />
                    <p className="text-sm">{ins.text}</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Player Analysis */}
          {selectedAnalysis.player_analysis && selectedAnalysis.player_analysis.length > 0 && (
            <Card><CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Users className="h-4 w-4 text-blue-600" /> Player Performance</CardTitle></CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-3">
                  {selectedAnalysis.player_analysis.map((p, i) => (
                    <div key={i} className="p-4 rounded-xl border hover:shadow-md transition-shadow">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <div className="h-9 w-9 rounded-full bg-gradient-to-br from-purple-500 to-violet-600 flex items-center justify-center text-white text-xs font-bold">{p.name?.[0]}</div>
                          <div><p className="text-sm font-semibold">{p.name}</p><p className="text-[10px] text-muted-foreground">{p.batch}</p></div>
                        </div>
                        <div className="text-right">
                          <p className={`text-xl font-display font-bold tabular-nums ${p.rating >= 8 ? 'text-emerald-600' : p.rating >= 7 ? 'text-blue-600' : 'text-amber-600'}`}>{p.rating}</p>
                          <p className="text-[9px] text-muted-foreground">/10 rating</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-4 gap-2 mb-3">
                        <div className="text-center"><p className="text-xs font-bold tabular-nums">{p.touches}</p><p className="text-[9px] text-muted-foreground">Touches</p></div>
                        <div className="text-center"><p className="text-xs font-bold tabular-nums">{p.pass_accuracy}%</p><p className="text-[9px] text-muted-foreground">Pass Acc</p></div>
                        <div className="text-center"><p className="text-xs font-bold tabular-nums">{p.distance_km}km</p><p className="text-[9px] text-muted-foreground">Distance</p></div>
                        <div className="text-center"><p className="text-xs font-bold tabular-nums">{p.sprints}</p><p className="text-[9px] text-muted-foreground">Sprints</p></div>
                      </div>
                      <p className="text-xs text-purple-700 dark:text-purple-400 bg-purple-50 dark:bg-purple-950/20 rounded-lg px-2.5 py-1.5 italic">"{p.ai_recommendation}"</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {selectedAnalysis.notes && (
            <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Notes</p><p className="text-sm">{selectedAnalysis.notes}</p></CardContent></Card>
          )}
        </div>
      )}
    </div>
  );
}
